import type { Map as LeafletMap } from "leaflet";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAX_PX, formatDistance, metersPerPixel, niceDistance } from "./scale.ts";

/** Desktop scale bar: a short bar with a round metric distance, updated on zoom and pan. */
export function MapScale({ map }: { map: LeafletMap | null }) {
  const { t } = useTranslation();
  const [bar, setBar] = useState<{ meters: number; px: number } | null>(null);

  useEffect(() => {
    if (!map) return;
    const update = () => {
      const mpp = metersPerPixel(map);
      const meters = niceDistance(mpp * MAX_PX);
      setBar(meters > 0 ? { meters, px: Math.round(meters / mpp) } : null);
    };
    update();
    map.on("move zoom resize", update);
    return () => void map.off("move zoom resize", update);
  }, [map]);

  if (!bar) return null;
  const label = formatDistance(bar.meters);
  return (
    <div className="map-scale ds-glass ds-text-caption-1">
      <span className="map-scale__sr">{t("map.scale", { distance: label })}</span>
      <div aria-hidden="true">
        <span className="map-scale__label">{label}</span>
        <span className="map-scale__bar" style={{ width: bar.px }} />
      </div>
    </div>
  );
}
